import { useState, useEffect, useMemo, useCallback, createContext, useContext, Fragment } from 'react';
import {
  Box, Container, Grid, Typography, Button, Card, CardContent, Chip,
  Divider, Paper, Stack, Avatar, List, ListItem, ListItemIcon,
  ListItemText, Accordion, AccordionSummary, AccordionDetails,
  Drawer, IconButton, Menu, MenuItem, useMediaQuery, useTheme, Fade, Tooltip,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {
  AccountBalance, School, People, Inventory, BarChart, Check,
  ExpandMore, ArrowForward, Calculate, Badge, Groups, Support,
  FamilyRestroom, BusinessCenter, Star, Menu as MenuIcon, Close,
  Storage, CheckCircle, Email, WorkspacePremium, Apartment,
  KeyboardArrowDown, Logout, RuleFolder, Assignment, Settings,
  ChevronLeft, ChevronRight, DarkModeOutlined, LightModeOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getTheme } from '../theme';

const API = process.env.REACT_APP_API_URL;
const INQUIRY_LABEL = { draft: 'Draft', pending: 'Under review', approved: 'Approved', rejected: 'Action needed' };

// ─── Brand tokens ──────────────────────────────────────────────────────────────
const NAVY = '#0a1931';
const BLUE = '#1565c0';
const GREEN = '#2e7d32';
const AMBER = '#e65100';
const LIGHT = '#f0f4ff';

// ─── Landing-only light/dark mode ───────────────────────────────────────────────
// The dark/light toggle lives ONLY on the public landing page; the app itself
// stays on its single light theme. All the landing's surfaces and text pull
// their colour from this mode-aware palette so the whole page flips together.
// Brand accents (BLUE/GREEN/AMBER and per-module colours) read well on both and
// are left as-is.
const makeColors = (dark) => dark ? {
  pageBg: '#0f1419', surface: '#141b23', surfaceAlt: '#0f151d', card: '#1a222c',
  cardBorder: '#2a333f', heading: '#e8eef6', body: '#aebccb', soft: '#8b9bab',
  navBg: 'rgba(16,22,30,0.9)', navBorder: '#232c37', footerBg: '#0d131b', footerText: '#8b9bab',
  heroGrad: 'linear-gradient(135deg,#141b24 0%,#101820 55%,#141c26 100%)',
  statsGrad: 'linear-gradient(135deg,#131a22 0%,#0f151d 100%)', bandBorder: '#232c37',
} : {
  pageBg: '#fff', surface: '#fff', surfaceAlt: LIGHT, card: '#fff',
  cardBorder: '#e8eaf0', heading: NAVY, body: '#44566b', soft: '#607d8b',
  navBg: 'rgba(255,255,255,0.85)', navBorder: '#e2e8f2', footerBg: '#eef2f8', footerText: '#5a708a',
  heroGrad: 'linear-gradient(135deg,#eef3fb 0%,#e6eefb 55%,#f3f7fd 100%)',
  statsGrad: 'linear-gradient(135deg,#eef3fb 0%,#e4ecf9 100%)', bandBorder: '#dfe7f2',
};

const LandingThemeCtx = createContext({ dark: false, toggle: () => {}, c: makeColors(false) });
const useLandingTheme = () => useContext(LandingThemeCtx);

// ─── Modules (reflects what's actually built) ───────────────────────────────────
const MODULES = [
  {
    icon: AccountBalance, color: '#1565c0', label: 'Finance',
    points: ['Fee billing, invoices, receipts & batch receipts', 'GL, general journals, banks & reconciliation', 'Balance Sheet, P&L, Trial Balance & Cash Flow', 'Budgets & live budget-vs-actual', 'Analysis dimensions & P&L by cost centre', 'VAT returns, WHT & typed posting groups', 'Learner statements with running balances', 'Posting periods & guided year-end close']
  },
  {
    icon: School, color: '#2e7d32', label: 'Academics (CBE)',
    points: ['Stages, streams, classes & learning areas', 'Term registration, promotions & subject sequence', 'Guideline-aware automatic timetabling & printable timetables', 'Assessment setup & score entry', 'Teacher role centre — class & subject classes', 'Student attendance (class & per-subject) with reasons', 'Per-subject analysis & curriculum coverage', 'Auto report forms (EE/ME/AE/BE)', 'Merit lists & 8-level CBC grading', 'LernAlytics performance analytics', 'AI career-pathway guidance']
  },
  {
    icon: People, color: '#6a1b9a', label: 'Human Resources',
    points: ['Employees, departments & pay grades', 'Leave management & ledgers', 'Performance Appraisal (goals → PIP)', 'Disciplinary & progressive warnings', 'Exit, clearance & certificate of service']
  },
  {
    icon: Calculate, color: '#c62828', label: 'Payroll',
    points: ['Formula-driven income tax & statutory', 'Statutory levies & custom transactions', 'Payslips & year-end tax forms', 'Periods, runs & statutory reports']
  },
  {
    icon: Inventory, color: '#4e342e', label: 'Supply Chain',
    points: ['Suppliers, purchase orders & LPOs', 'Receive/Invoice split & VAT ledger', 'Reorder points & AP/inventory reports', 'Store requisitions & transfers', 'Items, item journals & Fixed Assets']
  },
  {
    icon: FamilyRestroom, color: '#00695c', label: 'Parent Portal',
    points: ["Each child's fee balance any time", 'Statements, results & career insights', 'Message teachers with attachments', 'School events & payment confirmations', 'Mobile-first: phone, tablet or computer']
  },
  {
    icon: BarChart, color: AMBER, label: 'Reports & Letterheads',
    points: ['Finance, payroll, HR & academic reports', 'Merit lists & competency report forms', 'Every report on your letterhead', 'Print / PDF for ministries & regulators']
  },
  {
    icon: Settings, color: '#455a64', label: 'Setups & Security',
    points: ['Company details (logo, tax/registration no., bank)', 'Number series, currencies & posting groups', 'Users, groups & permission sets', 'Role-based access (View/Create/Edit/Delete/Post)', 'Multi-level approval workflows & inbox', 'Self-service data import (learners, staff, fees)']
  },
];

const HR_SUB = [
  { icon: Assignment, label: 'Performance Appraisal', desc: 'SPAS-style: weighted goals, supervisor approval, self & supervisor scoring, PIP.' },
  { icon: RuleFolder, label: 'Disciplinary', desc: 'Progressive warning ladder, gross-misconduct, suspension & salary deduction.' },
  { icon: Logout, label: 'Separation & Exit', desc: 'Ordered clearance, terminal dues, exit interview & certificate of service.' },
  { icon: People, label: 'Employees & Leave', desc: 'Records, departments, pay grades, leave applications, ledgers & reports.' },
];

const PERSONAS = [
  {
    icon: AccountBalance, color: BLUE, title: 'Government & Regional Offices',
    points: ['Ministry-ready compliance reports', 'Standardised data across schools', 'Audit trail on every transaction', 'Multi-school visibility']
  },
  {
    icon: BusinessCenter, color: GREEN, title: 'School Owners & Directors',
    points: ['Real-time fee-collection dashboards', 'Board-ready financial statements & budgets', 'One-click payroll & LPO procurement', 'Cost-per-learner visibility']
  },
  {
    icon: Badge, color: '#6a1b9a', title: 'Principals & Managers',
    points: ['Competency assessment, merit lists & report forms', 'Appraisal, leave & discipline', 'Fixed-asset register', 'Role-based access per staff']
  },
  {
    icon: FamilyRestroom, color: AMBER, title: 'Parents',
    points: ['Check fees without calling school', "View child's scores & career insights", 'Message teachers & get confirmations', 'Any device, anywhere']
  },
];

const SCHOOL_TIERS = [
  {
    key: 'community', icon: Groups, color: GREEN, name: 'Community & Public-Supported', tagline: 'Accessible tools for every school',
    desc: 'We understand grant and community funding. There is a place for every school regardless of fee level.',
    features: ['Finance & receipts', 'Competency-based academics & reports', 'Basic payroll & statutory', 'Parent fee-balance portal', '1 GB storage', 'Email & chat support'],
    cta: 'Ask About Our Community Rate', note: 'Flexible payment terms', popular: false
  },
  {
    key: 'private', icon: School, color: BLUE, name: 'Private Mid-Range', tagline: 'The complete toolkit',
    desc: 'For schools building their reputation — every department covered, strong parent features, board-level reporting.',
    features: ['All Finance modules', 'Full Academics & competency reports', 'HR, Leave, Appraisal & Payroll', 'Supply chain, LPOs & assets', 'Parent portal', 'Priority support'],
    cta: 'Get a Custom Quote', note: 'Most requested', popular: true
  },
  {
    key: 'premium', icon: WorkspacePremium, color: AMBER, name: 'Premium & Elite', tagline: 'Because your board expects it',
    desc: 'High-fee environments demand premium performance — isolation, custom branding and white-glove onboarding.',
    features: ['Everything in Mid-Range', 'Own isolated database', 'Granular roles', 'Dedicated account manager', 'White-glove onboarding', 'SLA support'],
    cta: 'Talk to Sales', note: 'White-glove onboarding', popular: false
  },
];

const FAQS = [
  { q: 'How do I set up a school?', a: 'Register and submit a school inquiry — tell us your school\'s details or request a demo. Our team reviews within 24 hours and sets up your database if approved. No direct creation — we ensure every school gets proper setup.' },
  { q: 'Does it support competency-based curricula?', a: 'Yes — configurable stages, strands and learning areas; assessment entry, weighting and report forms match your national formats, from early years through secondary.' },
  { q: 'Is each school\'s data separate?', a: 'Yes. Every school runs on its own isolated database (DB-per-client), provisioned by Dataport. One school can never see another\'s data.' },
  { q: 'What statutory deductions are handled?', a: 'Income tax, social security, health insurance and statutory levies are computed automatically with a formula-driven engine, plus any custom earnings/deductions — and rates are editable as policy changes.' },
  { q: 'Why no fixed price list?', a: 'A 1,400-learner boarding school is not the same as an 80-learner community school. We build a quote that matches your reality. A one-time activation fee (credited to your first invoice) covers onboarding.' },
  { q: 'Can you migrate our current data?', a: 'Yes — CSV templates for learners, staff and fee structures, with hands-on import help for mid-range and premium plans.' },
];

const STATS = [
  { icon: Calculate, value: '< 3 min', label: 'Average payroll run' },
  { icon: CheckCircle, value: '100%', label: 'Curriculum-aligned' },
  { icon: Storage, value: 'Per-school', label: 'Isolated database' },
  { icon: Support, value: '24 / 7', label: 'Uptime target' },
];

const INQUIRY_STEPS = [
  { n: 1, color: BLUE, emoji: '📋', title: 'Submit Your Details', desc: 'Register and tell us about your school — size, curriculum, staff. Or request a quick demo.' },
  { n: 2, color: GREEN, emoji: '✅', title: 'We Review & Approve', desc: 'Our team reviews your submission within 24 hours and ensures the setup is right for your school.' },
  { n: 3, color: AMBER, emoji: '⚙️', title: 'Database Provisioning', desc: 'Your own isolated database with sample data, ready within 5 working hours.' },
  { n: 4, color: '#6a1b9a', emoji: '🚀', title: "You're Live & Growing", desc: 'Log in, invite staff, configure setups, and start managing your school.' },
];

const NAV = [
  { key: 'home', label: 'Home' },
  { key: 'modules', label: 'Modules' },   // dropdown
  { key: 'audience', label: "Who It's For" },
  { key: 'pricing', label: 'Pricing' },
  { key: 'faq', label: 'FAQ' },
  { key: 'contact', label: 'Contact' },
];

const SLabel = ({ children, color = BLUE }) => (
  <Chip label={children} size="small" sx={{ bgcolor: color + '18', color, fontWeight: 700, mb: 1.5, fontSize: 11, letterSpacing: 1 }} />
);

const SectionShell = ({ children, bg }) => {
  const { c } = useLandingTheme();
  // Sections pass light literals (LIGHT / #fafbfc / #fff); remap them onto the
  // mode-aware palette so they turn dark together.
  const map = { [LIGHT]: c.surfaceAlt, '#fafbfc': c.surfaceAlt, '#fff': c.surface };
  const resolved = bg ? (map[bg] || bg) : c.surface;
  return (
    <Box sx={{ bgcolor: resolved, py: { xs: 5, md: 7 }, minHeight: '60vh' }}>
      <Container maxWidth="lg">{children}</Container>
    </Box>
  );
};

// ─── NavBar with a Modules dropdown ─────────────────────────────────────────────
const NavBar = ({ view, setView, loggedIn, onSignIn, onDashboard, inquiry, onInquiry }) => {
  const showInquiry = loggedIn && inquiry && inquiry.status !== 'completed';
  const [drawer, setDrawer] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { c, dark, toggle } = useLandingTheme();

  const ModeToggle = (
    <Tooltip title={dark ? 'Light mode' : 'Dark mode'}>
      <IconButton onClick={toggle} size="small" aria-label="Toggle light/dark mode" sx={{ color: c.body }}>
        {dark ? <LightModeOutlined fontSize="small" /> : <DarkModeOutlined fontSize="small" />}
      </IconButton>
    </Tooltip>
  );

  const go = (key) => { setView(key); setDrawer(false); setAnchor(null); window.scrollTo({ top: 0 }); };

  const NavBtn = ({ item }) => item.key === 'modules' ? (
    <Button onClick={e => setAnchor(e.currentTarget)} endIcon={<KeyboardArrowDown />}
      sx={{ color: view === 'modules' ? BLUE : '#4a5b6d', textTransform: 'none', fontWeight: 600, fontSize: 14, '&:hover': { color: NAVY, bgcolor: BLUE + '0f' } }}>
      {item.label}
    </Button>
  ) : (
    <Button onClick={() => go(item.key)}
      sx={{ color: view === item.key ? BLUE : '#4a5b6d', textTransform: 'none', fontWeight: 600, fontSize: 14, '&:hover': { color: NAVY, bgcolor: BLUE + '0f' } }}>
      {item.label}
    </Button>
  );

  return (
    <>
      <Box sx={{ position: 'sticky', top: 0, zIndex: 1200, bgcolor: c.navBg, backdropFilter: 'blur(12px)', borderBottom: `1px solid ${c.navBorder}`, boxShadow: '0 2px 14px rgba(10,25,49,0.06)' }}>
        <Container maxWidth="lg">
          <Box display="flex" alignItems="center" justifyContent="space-between" py={1.3}>
            <Box display="flex" alignItems="center" gap={1.2} sx={{ cursor: 'pointer' }} onClick={() => go('home')}>
              <Apartment sx={{ color: BLUE, fontSize: 30 }} />
              <Box>
                <Typography variant="h6" fontWeight={900} color={c.heading} lineHeight={1} letterSpacing={0.5}>EduMIS</Typography>
                <Typography variant="caption" color={c.soft} lineHeight={1} display="block" sx={{ fontSize: 10 }}>by Dataport Technologies</Typography>
              </Box>
            </Box>

            {!isMobile && <Stack direction="row" spacing={0.5}>{NAV.map(i => <NavBtn key={i.key} item={i} />)}</Stack>}

            <Stack direction="row" spacing={1} alignItems="center">
              {ModeToggle}
              {!isMobile && (loggedIn
                ? <>
                  {showInquiry && (
                    <Button onClick={onInquiry} size="small" startIcon={<Assignment fontSize="small" />}
                      sx={{ color: BLUE, textTransform: 'none', fontWeight: 600 }}>
                      My Inquiry
                      <Chip label={INQUIRY_LABEL[inquiry.status] || inquiry.status} size="small"
                        sx={{ ml: 0.8, height: 18, fontSize: 10, bgcolor: AMBER + '1a', color: AMBER }} />
                    </Button>
                  )}
                  <Button onClick={onDashboard} variant="contained" size="small" endIcon={<ArrowForward fontSize="small" />} sx={{ bgcolor: AMBER, '&:hover': { bgcolor: '#bf360c' }, fontWeight: 700, textTransform: 'none' }}>Dashboard</Button>
                </>
                : <>
                  <Button onClick={onSignIn} variant="outlined" size="small" startIcon={<Logout sx={{ transform: 'scaleX(-1)' }} />} sx={{ color: BLUE, borderColor: BLUE, '&:hover': { borderColor: '#0d47a1', bgcolor: BLUE + '0a' }, textTransform: 'none', fontWeight: 700 }}>Sign In</Button>
                  <Button onClick={onSignIn} variant="contained" size="small" sx={{ bgcolor: AMBER, '&:hover': { bgcolor: '#bf360c' }, fontWeight: 700, textTransform: 'none' }}>Start Free Trial</Button>
                </>)}
              {isMobile && <IconButton onClick={() => setDrawer(true)} sx={{ color: c.heading }}><MenuIcon /></IconButton>}
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Modules dropdown */}
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem disabled sx={{ opacity: 1, fontWeight: 700, fontSize: 12, color: 'text.secondary' }}>Jump to a module area</MenuItem>
        {MODULES.map(m => (
          <MenuItem key={m.label} onClick={() => go('modules')}>
            <ListItemIcon><m.icon sx={{ color: m.color }} fontSize="small" /></ListItemIcon>
            {m.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Mobile drawer */}
      <Drawer anchor="right" open={drawer} onClose={() => setDrawer(false)} PaperProps={{ sx: { width: 250, pt: 2 } }}>
        <Box px={2} pb={1} display="flex" justifyContent="space-between" alignItems="center">
          <Typography fontWeight={900} color={c.heading}>EduMIS</Typography>
          <IconButton onClick={() => setDrawer(false)}><Close /></IconButton>
        </Box>
        <Divider />
        <Stack sx={{ mt: 1 }}>
          {NAV.map(i => <Button key={i.key} onClick={() => go(i.key)} sx={{ justifyContent: 'flex-start', px: 2, py: 1.3, color: NAVY, textTransform: 'none', fontWeight: 600 }}>{i.label}</Button>)}
        </Stack>
        <Divider sx={{ my: 1.5 }} />
        <Box px={2}>
          {loggedIn
            ? <Stack spacing={1}>
              {showInquiry && (
                <Button fullWidth variant="outlined" onClick={() => { onInquiry(); setDrawer(false); }}
                  startIcon={<Assignment />} sx={{ textTransform: 'none', fontWeight: 700 }}>
                  My Inquiry · {INQUIRY_LABEL[inquiry.status] || inquiry.status}
                </Button>
              )}
              <Button fullWidth variant="contained" onClick={onDashboard} sx={{ bgcolor: AMBER, textTransform: 'none', fontWeight: 700 }} endIcon={<ArrowForward />}>Dashboard</Button>
            </Stack>
            : <Stack spacing={1}>
              <Button fullWidth variant="outlined" onClick={onSignIn} sx={{ textTransform: 'none', fontWeight: 600 }}>Sign In</Button>
              <Button fullWidth variant="contained" onClick={onSignIn} sx={{ bgcolor: AMBER, '&:hover': { bgcolor: '#bf360c' }, textTransform: 'none', fontWeight: 700 }}>Start Free Trial</Button>
            </Stack>}
        </Box>
      </Drawer>
    </>
  );
};

// ─── Features Carousel ──────────────────────────────────────────────────────────
const LATEST_FEATURES = [
  { emoji: '🧑‍🏫', title: 'Teacher Role Centre', desc: 'One home for every teacher: the class they lead and all the subject classes they teach, together — learners, results, attendance and analysis in a single place, scoped to exactly what they teach.', highlight: 'New' },
  { emoji: '🗓️', title: 'Student Attendance & Reasons', desc: 'Take whole-class or per-subject attendance — present by default — and record why a learner is out: sick, fees, games and more. Per-reason and per-learner reports export to Excel & PDF.', highlight: 'New' },
  { emoji: '📚', title: 'Curriculum Progress & Subject Analysis', desc: 'See how much of each subject\'s planned assessment is covered — formative, projects, summative and by strand — with grade breakdowns and performance-over-time trends for both CBC and 8-4-4.', highlight: 'New' },
  { emoji: '📆', title: 'Smart Timetabling', desc: 'Generate a whole-school timetable in minutes from your teacher allocations — built to the KICD senior-school guidelines: no subject repeats at the same time every day, mornings and afternoons stay balanced, similar subjects never sit back-to-back, STEM and Arts get their double lesson, and PE lands before a break. Reserve whole-school slots like Friday PPI, run 40- and 60-minute bell schedules side by side, and print class, teacher and master timetables on your letterhead.', highlight: 'New' },
  { emoji: '🔎', title: 'Search Anything — "Tell Me"', desc: 'A  universal search: jump to any page, list or report in one keystroke (Ctrl/⌘ + K). Permission-aware, so each user only finds what they can actually open.', highlight: 'New' },
  { emoji: '✅', title: 'Multi-Level Approval Workflows', desc: 'Route purchase orders, requisitions and journals through configurable approval chains. Approvers act from a single inbox and every decision is logged for audit.', highlight: 'New' },
  { emoji: '📈', title: 'Financial Statements & Budgets', desc: 'Board-ready Balance Sheet, Profit & Loss, Trial Balance and Cash Flow — plus budgets with live budget-vs-actual variance, all on your letterhead.', highlight: 'New' },
  { emoji: '🧭', title: 'Analysis Dimensions', desc: 'Tag any transaction with cost centres, projects or departments and slice your P&L by any dimension —  dimensional reporting.', highlight: 'New' },
  { emoji: '📥', title: 'Self-Service Data Import', desc: 'Bring learners, staff and fee structures across yourself with guided CSV templates and validation — go live faster, no waiting on manual data entry.', highlight: 'New' },
  { emoji: '🎯', title: 'AI Career-Pathway Guidance', desc: 'CBC pathway guidance for every learner — strengths and interests analysed into a clear, plain-language narrative that a teacher reviews and approves before parents ever see it.', highlight: 'Beta' },
  { emoji: '🏅', title: 'Merit Lists & CBC Grading', desc: 'Rank learners on an 8-level competency-based grading scale with subject-sequence merit lists — position by subject, stream and class, ready to print on your letterhead.', highlight: 'New' },
  { emoji: '📎', title: 'Message Attachments', desc: 'Attach report scans, receipts or letters to any message. Parents and teachers now exchange documents securely inside the portal — not on paper.', highlight: 'New' },
  { emoji: '📦', title: 'Smart Procurement & VAT', desc: 'Purchasing: separate Receive and Invoice steps, a full VAT ledger, automatic reorder points, and AP & inventory reports.', highlight: 'New' },
  { emoji: '💬', title: 'Parent–Teacher Messaging', desc: 'Parents write to class teachers straight from the portal — inbox, replies and unread notifications included. No more lost paper notes.', highlight: 'New' },
  { emoji: '📅', title: 'School Events & Notices', desc: 'Publish AGMs, visiting days, sports and closing ceremonies with dates, venues and charges. Parents see them instantly on their portal.', highlight: 'New' },
  { emoji: '📱', title: 'Mobile Parent Portal', desc: 'Built for phones: each child\'s fee balance at a glance, full statements with running balances, results and one-tap navigation.', highlight: 'New' },
  { emoji: '👨‍👩‍👧‍👦', title: 'Guardians, Done Right', desc: 'Every learner can have many guardians — father, mother, guardians and more — each with their own portal access, scoped to only their children.', highlight: 'New' },
  { emoji: '📊', title: 'Competency Assessment Setup', desc: 'Configure competencies, rubrics, and assessment types aligned to your competency-based framework.', highlight: 'Live' },
  { emoji: '💳', title: 'Online Fee Payment', desc: 'Parents pay fees online. Payments sync instantly to student ledgers.', highlight: 'Beta' },
  { emoji: '🎓', title: 'Report Form Auto-Generation', desc: 'Competency report forms (EE/ME/AE/BE) auto-populate and export to PDF ready for your ministry.', highlight: 'Live' },
  { emoji: '🔒', title: 'Role-Based Access', desc: 'Teachers, admins, finance staff, and parents each see only what they need. Granular permissions by module.', highlight: 'Live' },
];

// Compact auto-sliding "What's New" card that lives inside the hero, so the
// latest features share the first screen with the main pitch.
const HeroWhatsNew = () => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const next = () => setIdx((i) => (i + 1) % LATEST_FEATURES.length);
  const prev = () => setIdx((i) => (i - 1 + LATEST_FEATURES.length) % LATEST_FEATURES.length);

  // Auto-advance every 5s; pauses on hover/touch, manual nav resets the timer
  // (the interval depends on idx, so any change restarts it).
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % LATEST_FEATURES.length), 5000);
    return () => clearInterval(t);
  }, [paused, idx]);

  const feat = LATEST_FEATURES[idx];
  const colors = { 'New': '#ef5350', 'Live': '#66bb6a', 'Beta': '#ffca28' };
  const { c } = useLandingTheme();

  return (
    <Box
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)} onTouchEnd={() => setPaused(false)}
      sx={{ bgcolor: c.card, border: `1px solid ${c.cardBorder}`, borderRadius: 3, p: { xs: 2.5, md: 3 }, boxShadow: '0 12px 34px rgba(21,101,192,0.12)' }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Chip label="✨ What's New in EduMIS" size="small" sx={{ bgcolor: AMBER + '14', color: AMBER, fontWeight: 700, fontSize: 11 }} />
        <Box display="flex" gap={0.5}>
          <IconButton size="small" onClick={prev} sx={{ color: BLUE, bgcolor: BLUE + '0d', '&:hover': { bgcolor: BLUE + '1f' } }}><ChevronLeft fontSize="small" /></IconButton>
          <IconButton size="small" onClick={next} sx={{ color: BLUE, bgcolor: BLUE + '0d', '&:hover': { bgcolor: BLUE + '1f' } }}><ChevronRight fontSize="small" /></IconButton>
        </Box>
      </Box>

      <Fade in appear key={idx} timeout={500}>
        <Box sx={{ minHeight: { xs: 175, md: 190 } }}>
          <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
            <Typography sx={{ fontSize: 42, lineHeight: 1 }}>{feat.emoji}</Typography>
            <Box>
              <Typography variant="subtitle1" fontWeight={800} color={c.heading} lineHeight={1.25}>{feat.title}</Typography>
              <Chip label={feat.highlight} size="small" sx={{ mt: 0.5, height: 20, bgcolor: colors[feat.highlight] + '26', color: colors[feat.highlight], fontWeight: 700, fontSize: 10 }} />
            </Box>
          </Box>
          <Typography variant="body2" color={c.body} lineHeight={1.7}>{feat.desc}</Typography>
        </Box>
      </Fade>

      {/* Progress dots */}
      <Box display="flex" gap={0.8} mt={1.5} flexWrap="wrap">
        {LATEST_FEATURES.map((_, i) => (
          <Box key={i} onClick={() => setIdx(i)} sx={{
            width: idx === i ? 18 : 7, height: 7, borderRadius: 4,
            bgcolor: idx === i ? AMBER : '#cbd5e3',
            cursor: 'pointer', transition: 'all 0.3s',
          }} />
        ))}
      </Box>
    </Box>
  );
};

// ─── Sections ───────────────────────────────────────────────────────────────────
const Hero = ({ navigate, setView }) => { const { c } = useLandingTheme(); return (
  <>
    <Box sx={{ background: c.heroGrad, pt: { xs: 7, md: 10 }, pb: { xs: 7, md: 11 }, overflow: 'hidden', position: 'relative', borderBottom: `1px solid ${c.bandBorder}` }}>
      <Box sx={{ position: 'absolute', top: -80, right: -80, width: 400, height: 400, borderRadius: '50%', bgcolor: BLUE + '0d' }} />
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          <Grid size={{ xs: 12, md: 7 }}>
            <Chip label="🎓  Built for Modern Schools" size="small" sx={{ bgcolor: BLUE + '14', color: BLUE, fontWeight: 700, mb: 3, fontSize: 12 }} />
            <Typography variant="h2" fontWeight={900} color={c.heading} lineHeight={1.15} sx={{ fontSize: { xs: '2.1rem', md: '3.3rem' } }}>
              Run Your School<Box component="span" sx={{ color: BLUE }}> Without the </Box>Paperwork
            </Typography>
            <Typography variant="h6" color={c.body} mt={2.5} mb={4} fontWeight={400} lineHeight={1.7} sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, maxWidth: 560 }}>
              One platform for Finance, Academics, HR &amp; Payroll, Supply Chain and Parent Portals — each school on its own secure database.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button variant="contained" size="large" onClick={() => navigate('/login')} endIcon={<ArrowForward />} sx={{ bgcolor: AMBER, '&:hover': { bgcolor: '#bf360c' }, fontWeight: 700, px: 4, py: 1.5, borderRadius: 2, textTransform: 'none', fontSize: 16 }}>Start Free Trial</Button>
              <Button variant="contained" size="large" onClick={() => navigate('/login')} startIcon={<Logout sx={{ transform: 'scaleX(-1)' }} />} sx={{ bgcolor: BLUE, '&:hover': { bgcolor: '#0d47a1' }, fontWeight: 700, px: 4, py: 1.5, borderRadius: 2, textTransform: 'none', fontSize: 16 }}>Sign In</Button>
              <Button variant="outlined" size="large" onClick={() => setView('modules')} sx={{ color: c.heading, borderColor: c.cardBorder, '&:hover': { borderColor: BLUE, bgcolor: BLUE + '0a' }, fontWeight: 600, px: 4, py: 1.5, borderRadius: 2, textTransform: 'none', fontSize: 16 }}>Explore Modules</Button>
            </Stack>
            <Typography variant="caption" color={c.soft} mt={2} display="block">Low activation fee · credited to first invoice · 1 GB free</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <HeroWhatsNew />
          </Grid>
        </Grid>
      </Container>
    </Box>
    {/* Stats */}
    <Box sx={{ position: 'relative', overflow: 'hidden', background: c.statsGrad, borderTop: `1px solid ${c.bandBorder}`, borderBottom: `1px solid ${c.bandBorder}`, py: { xs: 5, md: 6 }, pb: { xs: 6, md: 8 } }}>
      <Box sx={{ position: 'absolute', top: -120, left: -60, width: 320, height: 320, borderRadius: '50%', bgcolor: BLUE + '08' }} />
      <Box sx={{ position: 'absolute', bottom: -140, right: -40, width: 360, height: 360, borderRadius: '50%', bgcolor: BLUE + '06' }} />
      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <Grid container spacing={{ xs: 2.5, md: 3 }}>
          {STATS.map((s, i) => (
            <Grid size={{ xs: 6, md: 3 }} key={i}>
              <Box sx={{
                p: { xs: 2.5, md: 3 }, height: '100%', textAlign: 'center',
                bgcolor: c.card, border: `1px solid ${c.cardBorder}`, borderRadius: 3,
                boxShadow: '0 6px 20px rgba(21,101,192,0.08)',
                '&:hover': { boxShadow: '0 10px 28px rgba(21,101,192,0.16)', transform: 'translateY(-5px)' },
                transition: 'all 0.25s',
              }}>
                <Box sx={{ width: 50, height: 50, mx: 'auto', mb: 1.4, borderRadius: '50%', bgcolor: BLUE + '12', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <s.icon sx={{ color: BLUE, fontSize: 24 }} />
                </Box>
                <Typography fontWeight={900} color={c.heading} noWrap sx={{ fontSize: { xs: '1.2rem', md: '1.7rem' }, lineHeight: 1.15 }}>{s.value}</Typography>
                <Box sx={{ width: 26, height: 3, borderRadius: 2, bgcolor: AMBER, mx: 'auto', my: 0.9 }} />
                <Typography variant="caption" color={c.soft} display="block" sx={{ lineHeight: 1.35, fontWeight: 500 }}>{s.label}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
    {/* School Inquiry Process */}
    <SectionShell bg="#fafbfc">
      <Box textAlign="center" mb={6}>
        <SLabel color={BLUE}>Getting Started</SLabel>
        <Typography variant="h4" fontWeight={800} color={c.heading} mb={1.5}>Simple onboarding · Expert setup</Typography>
        <Typography variant="body1" color="text.secondary" maxWidth={700} mx="auto" lineHeight={1.8}>
          We guide every school through a structured approval process. Our team reviews your request, ensures the right setup, and provisions your database within 5 working hours.
        </Typography>
      </Box>

      {/* Horizontal flow on desktop, vertical with down-arrows on mobile */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'center', md: 'stretch' } }}>
        {INQUIRY_STEPS.map((s, i) => (
          <Fragment key={s.n}>
            <Paper elevation={0} sx={{
              flex: 1, width: '100%', p: { xs: 2.5, md: 3 }, borderRadius: 3, textAlign: 'center',
              border: `1px solid ${s.color}30`, borderTop: `4px solid ${s.color}`, bgcolor: c.card,
              '&:hover': { boxShadow: `0 8px 28px ${s.color}22`, transform: 'translateY(-4px)' }, transition: 'all 0.25s',
            }}>
              <Box sx={{
                width: 52, height: 52, mx: 'auto', mb: 1.5, borderRadius: '50%',
                bgcolor: s.color, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 900, fontSize: 22, boxShadow: `0 6px 18px ${s.color}50`,
              }}>{s.n}</Box>
              <Typography sx={{ fontSize: '1.9rem', mb: 0.5, lineHeight: 1 }}>{s.emoji}</Typography>
              <Typography variant="subtitle1" fontWeight={800} color={c.heading} mb={1}>{s.title}</Typography>
              <Typography variant="body2" color="text.secondary" lineHeight={1.6}>{s.desc}</Typography>
            </Paper>
            {i < INQUIRY_STEPS.length - 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: { md: 0.5 }, py: { xs: 1, md: 0 } }}>
                <ArrowForward sx={{ color: '#90a4ae', fontSize: 26, transform: { xs: 'rotate(90deg)', md: 'none' } }} />
              </Box>
            )}
          </Fragment>
        ))}
      </Box>
    </SectionShell>

    {/* Home teaser - Module tiles */}
    <SectionShell bg={LIGHT}>
      <Box textAlign="center" mb={6}>
        <SLabel color={GREEN}>One System</SLabel>
        <Typography variant="h4" fontWeight={800} color={c.heading} mb={1.5}>Everything a school needs</Typography>
        <Typography variant="body1" color="text.secondary" maxWidth={620} mx="auto">
          All modules deeply integrated — data flows seamlessly across Finance, Academics, HR, Payroll, Supply Chain, and Parent Portals.
        </Typography>
      </Box>
      <Grid container spacing={2.5}>
        {MODULES.map(m => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={m.label}>
            <Card elevation={0} sx={{
              p: { xs: 1.5, md: 2.5 }, border: '1px solid ' + c.cardBorder, borderRadius: 2.5,
              height: '100%', minHeight: { xs: 150, md: 165 }, textAlign: 'center',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
              '&:hover': { borderColor: m.color, boxShadow: `0 6px 20px ${m.color}20`, transform: 'translateY(-4px)' },
              transition: 'all 0.3s', cursor: 'pointer',
            }} onClick={() => setView('modules')}>
              <Box sx={{ width: 50, height: 50, minHeight: 50, borderRadius: 2, bgcolor: m.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                <m.icon sx={{ color: m.color, fontSize: 26 }} />
              </Box>
              <Typography variant="subtitle2" fontWeight={700} color={c.heading} mb={0.5} sx={{ fontSize: { xs: 12.5, md: 14 } }}>{m.label}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                overflow: 'hidden', lineHeight: 1.4,
              }}>
                {m.points?.[0]}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
    </SectionShell>
  </>
); };

const ModulesSection = () => { const { c } = useLandingTheme(); return (
  <SectionShell>
    <Box textAlign="center" mb={5}>
      <SLabel color={GREEN}>Modules</SLabel>
      <Typography variant="h4" fontWeight={800} color={c.heading}>Deeply integrated, end to end</Typography>
      <Typography variant="body1" color="text.secondary" mt={1.5} maxWidth={620} mx="auto">Data entered once flows everywhere — a fee receipt updates the GL, the learner statement and the dashboard instantly.</Typography>
    </Box>
    <Grid container spacing={3}>
      {MODULES.map(m => (
        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={m.label}>
          <Paper elevation={0} sx={{ p: 3, border: '1px solid ' + c.cardBorder, borderRadius: 3, height: '100%', '&:hover': { borderColor: m.color, boxShadow: `0 4px 24px ${m.color}18`, transform: 'translateY(-3px)' }, transition: 'all 0.2s' }}>
            <Box sx={{ width: 50, height: 50, borderRadius: 2, bgcolor: m.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <m.icon sx={{ color: m.color, fontSize: 26 }} />
            </Box>
            <Typography variant="subtitle1" fontWeight={700} color={c.heading} mb={1}>{m.label}</Typography>
            <List dense disablePadding>
              {m.points.map(p => (
                <ListItem key={p} disablePadding sx={{ mb: 0.3 }}>
                  <ListItemIcon sx={{ minWidth: 22 }}><Check sx={{ fontSize: 15, color: m.color }} /></ListItemIcon>
                  <ListItemText primary={p} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      ))}
    </Grid>

    {/* HR spotlight (the FRD modules) */}
    <Box mt={8} pt={6} sx={{ borderTop: `2px solid ${c.cardBorder}` }}>
      <Box textAlign="center" mb={4}>
        <Chip label="Human Resources — Built to the FRD" sx={{ bgcolor: '#6a1b9a18', color: '#6a1b9a', fontWeight: 700, mb: 2 }} />
        <Typography variant="h5" fontWeight={700} color={c.heading}>Performance, Discipline & Separation</Typography>
        <Typography variant="body2" color="text.secondary" mt={1} maxWidth={620} mx="auto">
          A closer look at the Human Resources module — appraisal, discipline and exit, end to end.
        </Typography>
      </Box>
      <Grid container spacing={2.5}>
        {HR_SUB.map(h => (
          <Grid size={{ xs: 6, sm: 6, md: 3 }} key={h.label}>
            <Card elevation={0} sx={{
              p: { xs: 1.5, md: 2.5 }, border: '1px solid #6a1b9a20', borderRadius: 2.5,
              height: '100%', minHeight: { xs: 165, md: 185 }, textAlign: 'center',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              '&:hover': { borderColor: '#6a1b9a', boxShadow: '0 6px 20px #6a1b9a15', transform: 'translateY(-4px)' },
              transition: 'all 0.3s',
            }}>
              <Box sx={{ width: 48, height: 48, minHeight: 48, borderRadius: 2, bgcolor: '#6a1b9a15', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                <h.icon sx={{ color: '#6a1b9a', fontSize: 24 }} />
              </Box>
              <Typography variant="subtitle2" fontWeight={700} color={c.heading} mb={0.5} sx={{ fontSize: { xs: 12.5, md: 14 } }}>{h.label}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{
                display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical',
                overflow: 'hidden', lineHeight: 1.4,
              }}>{h.desc}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  </SectionShell>
); };

const AudienceSection = () => { const { c } = useLandingTheme(); return (
  <SectionShell bg={LIGHT}>
    <Box textAlign="center" mb={5}>
      <SLabel>Who It's For</SLabel>
      <Typography variant="h4" fontWeight={800} color={c.heading}>Every role in your education system</Typography>
    </Box>
    <Grid container spacing={3}>
      {PERSONAS.map(p => (
        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={p.title}>
          <Card elevation={0} sx={{ height: '100%', border: `2px solid ${p.color}22`, borderRadius: 3, '&:hover': { boxShadow: `0 8px 32px ${p.color}28`, transform: 'translateY(-4px)' }, transition: 'all 0.25s' }}>
            <CardContent sx={{ p: 3 }}>
              <Avatar sx={{ bgcolor: p.color + '18', width: 52, height: 52, mb: 2 }}><p.icon sx={{ color: p.color, fontSize: 28 }} /></Avatar>
              <Typography variant="subtitle1" fontWeight={800} color={c.heading} mb={1.5} lineHeight={1.3}>{p.title}</Typography>
              <List dense disablePadding>
                {p.points.map(pt => (
                  <ListItem key={pt} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}><Check sx={{ fontSize: 16, color: p.color }} /></ListItemIcon>
                    <ListItemText primary={pt} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  </SectionShell>
); };

const PricingSection = ({ navigate, setView }) => { const { c } = useLandingTheme(); return (
  <SectionShell>
    <Box textAlign="center" mb={2}>
      <SLabel color={AMBER}>Pricing</SLabel>
      <Typography variant="h4" fontWeight={800} color={c.heading}>Priced for your school — not a generic bracket</Typography>
      <Typography variant="body1" color="text.secondary" mt={1.5} maxWidth={620} mx="auto">A boarding school of 1,200 is not a community school of 80. Reach out and we'll build a quote together.</Typography>
    </Box>
    <Grid container spacing={3} justifyContent="center" sx={{ mt: 3 }}>
      {SCHOOL_TIERS.map(t => (
        <Grid size={{ xs: 12, md: 4 }} key={t.key}>
          <Card elevation={t.popular ? 8 : 1} sx={{ borderRadius: 3, height: '100%', border: t.popular ? `2px solid ${t.color}` : '1px solid ' + c.cardBorder, position: 'relative', overflow: 'visible', transform: t.popular ? { md: 'scale(1.04)' } : 'none' }}>
            {t.popular && <Chip label="Most Requested" icon={<Star sx={{ fontSize: '14px !important', color: 'white !important' }} />} sx={{ position: 'absolute', top: -16, left: '50%', transform: 'translateX(-50%)', bgcolor: t.color, color: 'white', fontWeight: 700, fontSize: 12 }} />}
            <CardContent sx={{ p: 3.5 }}>
              <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: t.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><t.icon sx={{ color: t.color, fontSize: 24 }} /></Box>
                <Box>
                  <Typography variant="caption" color={t.color} fontWeight={800} letterSpacing={1} display="block" textTransform="uppercase">{t.name}</Typography>
                  <Typography variant="caption" color="text.secondary" fontStyle="italic">{t.tagline}</Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" lineHeight={1.75} mb={2.5}>{t.desc}</Typography>
              <Button variant={t.popular ? 'contained' : 'outlined'} fullWidth size="large" onClick={() => setView('contact')} sx={{ mb: 2.5, textTransform: 'none', fontWeight: 700, borderRadius: 2, ...(t.popular ? { bgcolor: t.color, '&:hover': { bgcolor: t.color + 'cc' } } : { borderColor: t.color, color: t.color }) }}>{t.cta}</Button>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1.2}>
                {t.features.map(f => <Box key={f} display="flex" alignItems="flex-start" gap={1}><CheckCircle sx={{ fontSize: 16, color: t.color, flexShrink: 0, mt: 0.2 }} /><Typography variant="body2">{f}</Typography></Box>)}
              </Stack>
              {t.note && <Typography variant="caption" color={t.color} fontWeight={600} display="block" mt={2} textAlign="center">{t.note}</Typography>}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
    <Paper elevation={0} sx={{ mt: 5, p: 3, border: `1px dashed ${c.cardBorder}`, borderRadius: 3, bgcolor: c.surfaceAlt, textAlign: 'center' }}>
      <Storage sx={{ color: GREEN, fontSize: 30 }} />
      <Typography variant="subtitle1" fontWeight={800} color={c.heading}>1 GB free on every plan · own isolated database</Typography>
      <Typography variant="body2" color="text.secondary">Free 30-day trial. A low one-time activation fee, fully credited to your first invoice.</Typography>
      <Button variant="contained" sx={{ mt: 2, bgcolor: GREEN, '&:hover': { bgcolor: '#1b5e20' }, textTransform: 'none', fontWeight: 700, borderRadius: 2 }} endIcon={<ArrowForward />} onClick={() => navigate('/login')}>Start Free Trial</Button>
    </Paper>
  </SectionShell>
); };

const FaqSection = () => { const { c } = useLandingTheme(); return (
  <SectionShell>
    <Container maxWidth="md" disableGutters>
      <Box textAlign="center" mb={4}><SLabel color="#6a1b9a">FAQ</SLabel><Typography variant="h4" fontWeight={800} color={c.heading}>Common questions</Typography></Box>
      {FAQS.map((f, i) => (
        <Accordion key={i} elevation={0} sx={{ border: '1px solid ' + c.cardBorder, borderRadius: '12px !important', mb: 1.5, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3 }}><Typography fontWeight={700} color={c.heading}>{f.q}</Typography></AccordionSummary>
          <AccordionDetails sx={{ px: 3, pt: 0, pb: 2.5 }}><Typography variant="body2" color="text.secondary" lineHeight={1.8}>{f.a}</Typography></AccordionDetails>
        </Accordion>
      ))}
    </Container>
  </SectionShell>
); };

const ContactSection = ({ navigate }) => (
  <Box sx={{ background: `linear-gradient(135deg, ${NAVY} 0%, #0d2b5e 100%)`, py: { xs: 6, md: 9 }, minHeight: '60vh' }}>
    <Container maxWidth="lg">
      <Grid container spacing={6} alignItems="center">
        <Grid size={{ xs: 12, md: 6 }}>
          <SLabel color="#90caf9">Contact Us</SLabel>
          <Typography variant="h4" fontWeight={800} color="white" mb={2}>Let's find the right fit for your school</Typography>
          <Typography variant="body1" color="#b0bec5" mb={4} lineHeight={1.8}>Sign up and submit your school details. Our team will review your request within 24 hours and guide you through setup. Whether a small rural school or a large multi-campus network — we ensure every school gets the right foundation.</Typography>
          <Stack spacing={2.5}>
            <Box display="flex" gap={2} alignItems="center"><Email sx={{ color: '#64b5f6' }} /><Box><Typography variant="body2" color="white" fontWeight={600}>Email</Typography><Typography variant="body2" color="#78909c">EduMIS@dataportech.com</Typography></Box></Box>
            <Box display="flex" gap={2} alignItems="center"><BusinessCenter sx={{ color: '#64b5f6' }} /><Box><Typography variant="body2" color="white" fontWeight={600}>Company</Typography><Typography variant="body2" color="#78909c">Dataport Technologies — Nairobi, Kenya</Typography></Box></Box>
            <Box display="flex" gap={2} alignItems="center"><Support sx={{ color: '#64b5f6' }} /><Box><Typography variant="body2" color="white" fontWeight={600}>Support</Typography><Typography variant="body2" color="#78909c">Mon – Sat, 8 AM – 6 PM EAT</Typography></Box></Box>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 3, bgcolor: '#ffffff0a', border: '1px solid #ffffff15' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" fontWeight={800} color="white" mb={0.5}>Ready to get started?</Typography>
              <Typography variant="body2" color="#90a4ae" mb={3}>Pick the next step that works for you.</Typography>
              <Stack spacing={1.5}>
                <Button variant="contained" fullWidth size="large" onClick={() => navigate('/login')} endIcon={<ArrowForward />} sx={{ bgcolor: AMBER, '&:hover': { bgcolor: '#bf360c' }, textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 1.4 }}>Activate Free Trial</Button>
                <Button variant="outlined" fullWidth size="large" sx={{ color: 'white', borderColor: '#ffffff40', '&:hover': { bgcolor: '#ffffff10', borderColor: '#ffffffaa' }, textTransform: 'none', fontWeight: 600, borderRadius: 2, py: 1.4 }}>Schedule a Demo</Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

const DATAPORT_URL = 'https://www.dataportech.com/';

const Footer = ({ setView }) => { const { c } = useLandingTheme(); return (
  <Box sx={{ bgcolor: c.footerBg, borderTop: `1px solid ${c.navBorder}`, py: 4 }}>
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2} alignItems="center">
        <Box display="flex" alignItems="center" gap={1}>
          <Apartment sx={{ color: BLUE, fontSize: 22 }} />
          <Box>
            <Typography variant="subtitle2" color={c.heading} fontWeight={800} lineHeight={1}>EduMIS</Typography>
            <Typography variant="caption" color={c.footerText} sx={{ fontSize: 10 }}>
              by <Box component="a" href={DATAPORT_URL} target="_blank" rel="noopener noreferrer"
                sx={{ color: BLUE, fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Dataport Technologies</Box> · EduMIS.dataportech.com
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={2}>
          {NAV.filter(n => n.key !== 'home').map(n => <Typography key={n.key} variant="caption" fontWeight={600} color={c.footerText} sx={{ cursor: 'pointer', '&:hover': { color: BLUE } }} onClick={() => { setView(n.key); window.scrollTo({ top: 0 }); }}>{n.label}</Typography>)}
        </Stack>
        <Typography variant="caption" color={c.footerText}>
          © {new Date().getFullYear()} <Box component="a" href={DATAPORT_URL} target="_blank" rel="noopener noreferrer"
            sx={{ color: 'inherit', fontWeight: 600, textDecoration: 'none', '&:hover': { color: BLUE, textDecoration: 'underline' } }}>Dataport Technologies</Box>
        </Typography>
      </Box>
    </Container>
  </Box>
); };

// ─── Main ───────────────────────────────────────────────────────────────────────
const LandingContent = () => {
  const { c } = useLandingTheme();
  const navigate = useNavigate();
  const [view, setView] = useState('home');
  const [inquiry, setInquiry] = useState(null);
  const loggedIn = !!localStorage.getItem('token');

  // If signed in, surface the user's school inquiry (if any) in the nav bar.
  useEffect(() => {
    if (!loggedIn) return;
    axios.get(`${API}/api/school-inquiries/mine`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    }).then(r => setInquiry(r.data)).catch(() => {});
  }, [loggedIn]);

  return (
    <Box sx={{ bgcolor: c.pageBg, minHeight: '100vh', transition: 'background-color .3s' }}>
      <NavBar view={view} setView={setView} loggedIn={loggedIn}
        onSignIn={() => navigate('/login')} onDashboard={() => navigate('/dashboard')}
        inquiry={inquiry} onInquiry={() => navigate('/school-inquiry')} />
      {view === 'home' && <Hero navigate={navigate} setView={setView} />}
      {view === 'modules' && <ModulesSection />}
      {view === 'audience' && <AudienceSection />}
      {view === 'pricing' && <PricingSection navigate={navigate} setView={setView} />}
      {view === 'faq' && <FaqSection />}
      {view === 'contact' && <ContactSection navigate={navigate} />}
      <Footer setView={setView} />
    </Box>
  );
};

// Landing-only light/dark: its own persisted mode (separate key from the app,
// which stays light), its own MUI ThemeProvider, and the palette context that
// every section reads. The app outside this page is unaffected.
const LANDING_MODE_KEY = 'landingColorMode';
const initialLandingMode = () => {
  // Always start in light mode; only honour an explicit dark choice the visitor
  // made before (we ignore the OS preference so the page opens light by default).
  return localStorage.getItem(LANDING_MODE_KEY) === 'dark' ? 'dark' : 'light';
};

const Landing = () => {
  const [mode, setMode] = useState(initialLandingMode);
  const dark = mode === 'dark';

  // Persist as a side effect (never inside the state updater).
  useEffect(() => { localStorage.setItem(LANDING_MODE_KEY, mode); }, [mode]);

  const c = useMemo(() => makeColors(dark), [dark]);
  const theme = useMemo(() => getTheme(mode), [mode]);
  const toggle = useCallback(() => setMode(m => (m === 'dark' ? 'light' : 'dark')), []);
  const value = useMemo(() => ({ dark, c, toggle }), [dark, c, toggle]);

  return (
    <LandingThemeCtx.Provider value={value}>
      <ThemeProvider theme={theme}>
        <LandingContent />
      </ThemeProvider>
    </LandingThemeCtx.Provider>
  );
};

export default Landing;
